import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Link } from '@ui/components/Link'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ui/v2/Table/Table'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'

import { Operator, PageQuery, SearchQuery } from '@utils/ApiClient'
import { formatDate } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'
import { extractValueFromFilterQuery } from '@utils/extractValueFromFilterQuery'

import { CampaignStatusEnum } from '@model/CampaignStatusEnum'
import { CampaignType } from '@model/CampaignType'
import { FilterFields, FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { IBContestBadgeMap, StatusBadge } from '@shared/components/StatusBadge/StatusBadge'

import { useRewardsHubHistoryOutletContext } from './RewardsHub.page'
import { useCampaignsHistory } from './hooks/useCampaignsHistory'

import styles from './CampaignsHistoryTab.module.scss'

export const getCampaignResultsPath = (
  campaignId: string,
  tradingAccountName: string,
  campaignType?: number
) => {
  const base =
    campaignType === CampaignType.ClientContest
      ? 'contest'
      : campaignType === CampaignType.TradingPool
      ? 'trading-pool'
      : 'rebate'
  return `/dashboard/traders-room/campaign-results/${base}/${tradingAccountName}/${campaignId}`
}

const mapFiltersToCampaignHistoryQuery = (filters: FilterQueryProps): PageQuery => {
  const search: SearchQuery = {}
  const getFirstSelected = (value?: string) =>
    (value || '')
      .split(' ')
      .map((x) => x.trim())
      .find(Boolean)
  const addSingleEquals = (field: string, value?: string, quoted = true) => {
    const selectedValue = getFirstSelected(value)
    if (!selectedValue) {
      return
    }

    search[field] = quoted ? `'${selectedValue}'` : selectedValue
  }

  const campaignId = extractValueFromFilterQuery(filters[FilterOptions.CampaignId])
  addSingleEquals('Campaign', campaignId)

  const tradingAccount = extractValueFromFilterQuery(filters[FilterOptions.TradingAccountId])
  addSingleEquals('TradingAccount', tradingAccount)

  const dateFrom = extractValueFromFilterQuery(filters[FilterFields.ClientDateFrom])
  if (dateFrom) {
    search.StartDate = {
      operator: Operator.MORE_OR_EQUAL,
      value: dateFrom,
    }
  }

  const dateTo = extractValueFromFilterQuery(filters[FilterFields.ClientDateTo])
  if (dateTo) {
    search.EndDate = {
      operator: Operator.LESS_OR_EQUAL,
      value: dateTo,
    }
  }

  const currency = extractValueFromFilterQuery(filters[FilterOptions.Currency])
  addSingleEquals('Currency', currency)

  const status = extractValueFromFilterQuery(filters[FilterOptions.Status])
  addSingleEquals('Status', status, false)

  return {
    search,
  }
}

export const CampaignsHistoryTab = () => {
  const { t } = useTranslation()
  const { isHistoryFilterVisible } = useRewardsHubHistoryOutletContext()
  const [searchFilters, setSearchFilters] = useState<FilterQueryProps>({})

  const campaignHistoryQuery = useMemo<PageQuery>(
    () => ({
      ...mapFiltersToCampaignHistoryQuery(searchFilters),
    }),
    [searchFilters]
  )

  const { campaignsHistory, isLoading } = useCampaignsHistory(campaignHistoryQuery)
  const { campaignsHistory: allCampaignsHistory } = useCampaignsHistory()

  const isMobile = useWindowResize()
  const [expandedId, setExpandedId] = useState<string>('')

  const handleSearchFilters = useCallback((nextFilters?: FilterQueryProps) => {
    setSearchFilters(nextFilters || {})
  }, [])

  const campaignOptions = useMemo<NameDto<string>[]>(() => {
    const uniqueCampaigns = new Map<string, NameDto<string>>()

    allCampaignsHistory.forEach((item) => {
      uniqueCampaigns.set(item.campaign.id, {
        id: item.campaign.id,
        name: item.campaign.name,
      })
    })

    return Array.from(uniqueCampaigns.values())
  }, [allCampaignsHistory])

  const tradingAccountOptions = useMemo<NameDto<string>[]>(() => {
    const uniqueAccounts = new Map<string, NameDto<string>>()

    allCampaignsHistory.forEach((item) => {
      uniqueAccounts.set(item.tradingAccount.id, {
        id: item.tradingAccount.name,
        name: item.tradingAccount.name,
      })
    })

    return Array.from(uniqueAccounts.values())
  }, [allCampaignsHistory])

  const currencyOptions = useMemo<NameDto<string>[]>(() => {
    const uniqueCurrencies = new Map<string, NameDto<string>>()

    allCampaignsHistory.forEach((item) => {
      uniqueCurrencies.set(item.currency.id, {
        id: item.currency.id,
        name: item.currency.id,
      })
    })

    return Array.from(uniqueCurrencies.values())
  }, [allCampaignsHistory])

  const statusOptions = useMemo<NameDto<number>[]>(() => {
    const uniqueStatuses = new Map<number, NameDto<number>>()

    allCampaignsHistory.forEach((item) => {
      uniqueStatuses.set(item.status.id, {
        id: item.status.id,
        name: item.status.name,
      })
    })

    return Array.from(uniqueStatuses.values())
  }, [allCampaignsHistory])

  return (
    <Loading isLoading={isLoading}>
      <TableFilter
        isVisible={isHistoryFilterVisible}
        searchFilters={searchFilters}
        filterOptions={[
          FilterOptions.CampaignId,
          FilterOptions.TradingAccountId,
          FilterOptions.Date,
          FilterOptions.Currency,
          FilterOptions.Status,
        ]}
        campaignSearchProps={{ options: campaignOptions }}
        tradingAccountSearchProps={{ options: tradingAccountOptions }}
        currencyOptions={currencyOptions}
        statusOptions={statusOptions}
        customLabels={{
          [FilterOptions.CampaignId]: t('Campaigns.Campaign name'),
          [FilterOptions.TradingAccountId]: t('Campaigns.Account No'),
          [FilterOptions.Date]: t('Date'),
          [FilterOptions.Currency]: t('Campaigns.Currency'),
          [FilterOptions.Status]: t('Campaigns.Status'),
        }}
        setSearchFilters={handleSearchFilters}
      />

      {isMobile ? (
        <Box flex direction='column' gap='x16' fullWidth>
          {campaignsHistory.map((campaign) => {
            const rowId = `${campaign.campaign.id}-${campaign.tradingAccount.id}-${campaign.startDate}`
            const isExpanded = expandedId === rowId

            return (
              <Box
                key={rowId}
                className={styles.card}
                gap='x16'
                onClick={() => setExpandedId(isExpanded ? '' : rowId)}
              >
                <Box flex align='center' justify='space-between' fullWidth>
                  <Box flex align='center' gap='x8'>
                    <ActionIcon icon={isExpanded ? ChevronIcon.Up : ChevronIcon.Down} size='x24' />
                    <Body2 semibold>{campaign.campaign.name}</Body2>
                  </Box>
                  <StatusBadge
                    id={campaign.isEnded ? CampaignStatusEnum.Disabled : campaign.status.id}
                    badgeMap={IBContestBadgeMap}
                  />
                </Box>

                {isExpanded && (
                  <>
                    <hr className={styles.divider} />
                    <Box flex direction='column' gap='x8'>
                      <div className={styles.detailRow}>
                        <Body2 color='text-low-em'>{t('Campaigns.Start Date')}</Body2>
                        <Body2>
                          {formatDate(campaign.startDate, {
                            formatType: 'dd/MM/yyyy',
                            withTime: false,
                          })}
                        </Body2>
                      </div>
                      <div className={styles.detailRow}>
                        <Body2 color='text-low-em'>{t('Campaigns.End Date')}</Body2>
                        <Body2>
                          {formatDate(campaign.endDate, {
                            formatType: 'dd/MM/yyyy',
                            withTime: false,
                          })}
                        </Body2>
                      </div>
                      <div className={styles.detailRow}>
                        <Body2 color='text-low-em'>{t('Campaigns.Currency')}</Body2>
                        <Body2>{campaign.currency.id}</Body2>
                      </div>
                      <div className={styles.detailRow}>
                        <Body2 color='text-low-em'>{t('Campaigns.Account No')}</Body2>
                        <Body2>{`${campaign.currency.id}-${campaign.tradingAccount.name}`}</Body2>
                      </div>
                    </Box>
                    <div className={styles.resultsLinkWrap}>
                      <Link
                        to={getCampaignResultsPath(
                          campaign.campaign.id,
                          campaign.tradingAccount.name,
                          campaign.type?.id
                        )}
                      >
                        {t('Campaigns.View results')}
                      </Link>
                    </div>
                  </>
                )}
              </Box>
            )
          })}
        </Box>
      ) : (
        <Table isLoading={isLoading} noResults={!campaignsHistory.length}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Campaigns.Campaign name')}</TableHead>
              <TableHead>{t('Campaigns.Campaign Account No')}</TableHead>
              <TableHead>{t('Campaigns.Start Date')}</TableHead>
              <TableHead>{t('Campaigns.End Date')}</TableHead>
              <TableHead>{t('Campaigns.Currency')}</TableHead>
              <TableHead align='center'>{t('Campaigns.Status')}</TableHead>
              <TableHead align='center'>{t('Campaigns.Results')}</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {campaignsHistory.map((campaign) => (
              <TableRow
                key={`${campaign.campaign.id}-${campaign.tradingAccount.id}-${campaign.startDate}`}
              >
                <TableCell>{campaign.campaign.name}</TableCell>
                <TableCell>{campaign.tradingAccount.name}</TableCell>
                <TableCell>
                  {formatDate(campaign.startDate, {
                    formatType: 'dd/MM/yyyy',
                    withTime: false,
                  })}
                </TableCell>
                <TableCell>
                  {formatDate(campaign.endDate, {
                    formatType: 'dd/MM/yyyy',
                    withTime: false,
                  })}
                </TableCell>
                <TableCell>{campaign.currency.id}</TableCell>
                <TableCell hasTruncate={false} align='center'>
                  <StatusBadge
                    id={campaign.isEnded ? CampaignStatusEnum.Disabled : campaign.status.id}
                    badgeMap={IBContestBadgeMap}
                  />
                </TableCell>
                <TableCell align='center'>
                  <Link
                    to={getCampaignResultsPath(
                      campaign.campaign.id,
                      campaign.tradingAccount.name,
                      campaign.type?.id
                    )}
                  >
                    {t('View')}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Loading>
  )
}
